<template>
  <div>
    <modern-loader :loading="loadingStuff" :message="loadIngStuffText" />
    <app-template />
    <div class="return-to-play-page uk-container-large">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <h1>
            <i class="bi bi-activity"></i>
            Return-to-Play {{
              mainTab === 'my-exercises' ? 'Exercises' :
                mainTab === 'exercise-library' ? 'Exercise Library' :
                  'Dashboard'
            }}
          </h1>
          <p class="subtitle">{{
            mainTab === 'my-exercises' ? 'Track your daily rehabilitation exercises' :
              mainTab === 'exercise-library' ? 'Manage rehabilitation exercises for player programs' :
                'Monitor and manage player rehabilitation protocols'
          }}</p>
        </div>
        <div class="header-actions" v-if="mainTab === 'dashboard' && canViewDashboard">
          <button class="btn btn-outline-primary" @click="refreshData">
            <i class="bi bi-arrow-clockwise"></i>
            Refresh
          </button>
          <button class="btn btn-outline-secondary" @click="exportToCsv">
            <i class="bi bi-download"></i>
            Export
          </button>
        </div>
      </div>

      <!-- Main Tab Navigation (for players with active RTP or staff) -->
      <div v-if="(playerHasActiveRTP || canViewDashboard) && (isPlayer || canViewDashboard)" class="main-tab-nav">
        <button v-if="playerHasActiveRTP || isPlayer" :class="['main-tab-btn', { active: mainTab === 'my-exercises' }]"
          @click="mainTab = 'my-exercises'">
          <i class="bi bi-list-check"></i>
          My Exercises
        </button>
        <button v-if="canViewDashboard" :class="['main-tab-btn', { active: mainTab === 'dashboard' }]"
          @click="mainTab = 'dashboard'">
          <i class="bi bi-speedometer2"></i>
          Squad Dashboard
        </button>
        <button v-if="canManageExercises" :class="['main-tab-btn', { active: mainTab === 'exercise-library' }]"
          @click="mainTab = 'exercise-library'">
          <i class="bi bi-journal-medical"></i>
          Exercise Library
        </button>
      </div>

      <!-- My Exercises View (for players) -->
      <div v-if="mainTab === 'my-exercises'" class="my-exercises-section">
        <MyRTP :playerId="userData?.id" />
      </div>

      <!-- Exercise Library View (for staff) -->
      <div v-if="mainTab === 'exercise-library'" class="exercise-library-section">
        <RTPExerciseLibrary />
      </div>

      <!-- Dashboard View (for staff) - only show if dashboard tab is active -->
      <template v-if="mainTab === 'dashboard' && canViewDashboard">

        <!-- Summary Stats -->
        <div class="summary-stats">
          <div class="stat-card total">
            <div class="stat-icon">
              <i class="bi bi-people"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ summary.total }}</span>
              <span class="stat-label">Active RTPs</span>
            </div>
          </div>

          <div v-for="phase in 5" :key="phase" class="stat-card" :class="'phase-' + phase">
            <div class="stat-icon"
              :style="{ backgroundColor: getPhaseInfo(phase).bgColor, color: getPhaseInfo(phase).color }">
              <span>P{{ phase }}</span>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ summary.byPhase[phase] || 0 }}</span>
              <span class="stat-label">{{ getPhaseInfo(phase).name }}</span>
            </div>
          </div>

          <div class="stat-card overdue" v-if="summary.overdueReviews > 0">
            <div class="stat-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ summary.overdueReviews }}</span>
              <span class="stat-label">Overdue Reviews</span>
            </div>
          </div>

          <div class="stat-card circa-phv" v-if="summary.circaPhvCount > 0">
            <div class="stat-icon">
              <i class="bi bi-person-exclamation"></i>
            </div>
            <div class="stat-content">
              <span class="stat-value">{{ summary.circaPhvCount }}</span>
              <span class="stat-label">Circa-PHV Players</span>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-section">
          <div class="filter-group">
            <label>Team:</label>
            <select v-model="filterTeam" class="form-select" @change="loadDashboardData">
              <option value="">All Teams</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.teamName }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>Phase:</label>
            <select v-model="filterPhase" class="form-select">
              <option value="">All Phases</option>
              <option v-for="phase in 5" :key="phase" :value="phase">
                P{{ phase }}: {{ getPhaseInfo(phase).name }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>View:</label>
            <div class="btn-group view-toggle">
              <button :class="['btn', viewMode === 'grouped' ? 'btn-primary' : 'btn-outline-primary']"
                @click="viewMode = 'grouped'">
                <i class="bi bi-collection"></i> Grouped
              </button>
              <button :class="['btn', viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary']"
                @click="viewMode = 'table'">
                <i class="bi bi-table"></i> Table
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p>Loading RTP data...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredProtocols.length === 0" class="empty-state">
          <i class="bi bi-clipboard-check"></i>
          <h4>No Active RTP Protocols</h4>
          <p>There are no players currently on a Return-to-Play protocol.</p>
          <p class="text-muted mt-3">
            <small>To start an RTP protocol, go to a player's injury record and click "Start RTP Protocol".</small>
          </p>
        </div>

        <!-- Grouped View -->
        <div v-else-if="viewMode === 'grouped'" class="grouped-view">
          <div v-for="phase in 5" :key="phase" v-show="!filterPhase || filterPhase == phase" class="phase-group"
            :class="{ 'empty': !groupedProtocols[phase] || groupedProtocols[phase].players.length === 0 }">
            <div class="group-header" :style="{ borderLeftColor: getPhaseInfo(phase).color }">
              <div class="group-title">
                <span class="phase-badge" :style="{ backgroundColor: getPhaseInfo(phase).color }">
                  P{{ phase }}
                </span>
                <h3>{{ getPhaseInfo(phase).name }}</h3>
                <span class="player-count">
                  {{ groupedProtocols[phase]?.players.length || 0 }} player{{ groupedProtocols[phase]?.players.length
                    !== 1 ? 's' : '' }}
                </span>
              </div>
              <p class="group-description">{{ getPhaseInfo(phase).description }}</p>
            </div>

            <div v-if="groupedProtocols[phase]?.players.length > 0" class="players-grid">
              <div v-for="player in groupedProtocols[phase].players" :key="player.injury_id" class="player-card" :class="{
                'overdue': player.days_until_review < 0,
                'circa-phv': player.maturity_at_injury === 'Circa-PHV'
              }" @click="openPlayerDetail(player)">
                <div class="card-header">
                  <img v-if="player.player_image" :src="getImgUrl(player.player_image)" :alt="player.player_name"
                    class="player-avatar" />
                  <div v-else class="player-avatar-placeholder">
                    {{ getInitials(player.player_name) }}
                  </div>
                  <div class="player-info">
                    <span class="player-name">{{ player.player_name }}</span>
                    <span class="team-name">{{ player.teamName || player.age_group }}</span>
                  </div>
                  <div v-if="player.maturity_at_injury === 'Circa-PHV'" class="maturity-flag">
                    <i class="bi bi-exclamation-triangle"></i>
                  </div>
                </div>

                <div class="card-body">
                  <div class="injury-type">
                    <i class="bi bi-bandaid"></i>
                    {{ player.injury_type }}
                  </div>

                  <div class="card-stats">
                    <div class="stat">
                      <span class="stat-label">Days in Phase</span>
                      <span class="stat-value" :class="{ 'warning': player.days_in_phase > 14 }">
                        {{ player.days_in_phase || 0 }}
                      </span>
                    </div>
                    <div class="stat">
                      <span class="stat-label">Phase Lead</span>
                      <span class="stat-value">{{ player.current_phase_lead || '-' }}</span>
                    </div>
                  </div>

                  <div v-if="player.days_until_review !== null" class="review-status"
                    :class="{ 'overdue': player.days_until_review < 0 }">
                    <i :class="player.days_until_review < 0 ? 'bi bi-exclamation-circle' : 'bi bi-calendar-check'"></i>
                    <span v-if="player.days_until_review < 0">
                      Review overdue by {{ Math.abs(player.days_until_review) }} days
                    </span>
                    <span v-else-if="player.days_until_review === 0">
                      Review due today
                    </span>
                    <span v-else>
                      Review in {{ player.days_until_review }} days
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="no-players">
              <p>No players in this phase</p>
            </div>
          </div>
        </div>

        <!-- Table View -->
        <div v-else class="table-view">
          <table class="rtp-table">
            <thead>
              <tr>
                <th @click="sortBy('player_name')" class="sortable">
                  Player
                  <i v-if="sortField === 'player_name'"
                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                </th>
                <th>Team</th>
                <th @click="sortBy('injury_type')" class="sortable">
                  Injury
                  <i v-if="sortField === 'injury_type'"
                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                </th>
                <th @click="sortBy('rtp_phase')" class="sortable">
                  Phase
                  <i v-if="sortField === 'rtp_phase'"
                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                </th>
                <th @click="sortBy('days_in_phase')" class="sortable">
                  Days
                  <i v-if="sortField === 'days_in_phase'"
                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                </th>
                <th>Phase Lead</th>
                <th>Maturity</th>
                <th @click="sortBy('days_until_review')" class="sortable">
                  Review
                  <i v-if="sortField === 'days_until_review'"
                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="player in sortedProtocols" :key="player.injury_id" :class="{
                'overdue-row': player.days_until_review < 0,
                'circa-phv-row': player.maturity_at_injury === 'Circa-PHV'
              }">
                <td class="player-cell">
                  <img v-if="player.player_image" :src="getImgUrl(player.player_image)" :alt="player.player_name"
                    class="player-avatar-sm" />
                  <div v-else class="player-avatar-placeholder-sm">
                    {{ getInitials(player.player_name) }}
                  </div>
                  <span>{{ player.player_name }}</span>
                </td>
                <td>{{ player.teamName || player.age_group }}</td>
                <td>{{ player.injury_type }}</td>
                <td>
                  <span class="phase-badge-sm" :style="{ backgroundColor: getPhaseInfo(player.rtp_phase).color }">
                    P{{ player.rtp_phase }}
                  </span>
                </td>
                <td>
                  <span :class="{ 'text-warning fw-bold': player.days_in_phase > 14 }">
                    {{ player.days_in_phase || 0 }}
                  </span>
                </td>
                <td>{{ player.current_phase_lead || '-' }}</td>
                <td>
                  <span v-if="player.maturity_at_injury" class="maturity-badge-sm"
                    :class="'maturity-' + player.maturity_at_injury.toLowerCase().replace('-', '')">
                    {{ player.maturity_at_injury }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td>
                  <span v-if="player.days_until_review !== null"
                    :class="{ 'text-danger': player.days_until_review < 0 }">
                    {{ player.days_until_review < 0 ? Math.abs(player.days_until_review) + 'd overdue' :
                      player.days_until_review + 'd' }} </span>
                      <span v-else>-</span>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline-primary" @click="openPlayerDetail(player)">
                    <i class="bi bi-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Player Detail Modal -->
        <b-modal v-if="selectedPlayer" v-model="showPlayerModal" size="xl"
          :title="selectedPlayer.player_name + ' - RTP Protocol'" :hide-footer="true" modal-class="rtp-player-modal">
          <div class="player-detail-content">
            <!-- Player Header -->
            <div class="detail-header">
              <div class="player-profile">
                <img v-if="selectedPlayer.player_image" :src="getImgUrl(selectedPlayer.player_image)"
                  :alt="selectedPlayer.player_name" class="profile-avatar" />
                <div v-else class="profile-avatar-placeholder">
                  {{ getInitials(selectedPlayer.player_name) }}
                </div>
                <div class="profile-info">
                  <h4>{{ selectedPlayer.player_name }}</h4>
                  <p>{{ selectedPlayer.teamName || selectedPlayer.age_group }}</p>
                </div>
              </div>
              <div class="injury-info">
                <span class="injury-type">{{ selectedPlayer.injury_type }}</span>
                <span class="injury-date">Since {{ formatDate(selectedPlayer.injury_date) }}</span>
              </div>
            </div>

            <!-- Status Bar -->
            <div class="status-bar">
              <div class="status-item">
                <label>Playing Status:</label>
                <select v-model="selectedPlayer.playing_status"
                  @change="updatePlayerStatus('playing_status', selectedPlayer.playing_status)"
                  class="form-select form-select-sm">
                  <option :value="0">Can't play</option>
                  <option :value="1">Ready to play</option>
                </select>
              </div>
              <div class="status-item">
                <label>Training Status:</label>
                <select v-model="selectedPlayer.training_status"
                  @change="updatePlayerStatus('training_status', selectedPlayer.training_status)"
                  class="form-select form-select-sm">
                  <option value="Unable">Unable to train</option>
                  <option value="Partially">Can partially train</option>
                  <option value="Fully">Can fully train</option>
                </select>
              </div>
              <div v-if="selectedPlayer.estimate_return_date" class="status-item">
                <label>Est. Return:</label>
                <span class="status-value">{{ selectedPlayer.estimate_return_date }}</span>
              </div>
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs detail-tabs">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: detailTab === 'tracker' }" @click="detailTab = 'tracker'"
                  href="javascript:;">
                  <i class="bi bi-speedometer2"></i> Phase Tracker
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: detailTab === 'timeline' }" @click="detailTab = 'timeline'"
                  href="javascript:;">
                  <i class="bi bi-clock-history"></i> Timeline
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: detailTab === 'exercises' }" @click="detailTab = 'exercises'"
                  href="javascript:;">
                  <i class="bi bi-list-check"></i> Exercises
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: detailTab === 'injury-details' }"
                  @click="detailTab = 'injury-details'" href="javascript:;">
                  <i class="bi bi-file-medical"></i> Injury Details
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: detailTab === 'physio-appointments' }"
                  @click="detailTab = 'physio-appointments'; loadPlayerAppointments()" href="javascript:;">
                  <i class="bi bi-calendar2-heart"></i> Physio
                  <span v-if="playerAppointments.length > 0" class="appointment-badge">{{ playerAppointments.length
                    }}</span>
                </a>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content">
              <div v-if="detailTab === 'tracker'" class="tab-pane active">
                <RTPPhaseTracker v-if="selectedPlayer.injury_id" :injuryId="selectedPlayer.injury_id"
                  :userData="userData" @phase-updated="onPhaseUpdated" @rtp-completed="onRtpCompleted" />
              </div>

              <div v-if="detailTab === 'timeline'" class="tab-pane active">
                <RTPTimeline :injuryDate="selectedPlayer.injury_date" :injuryType="selectedPlayer.injury_type"
                  :injuryReportedBy="{ name: selectedPlayer.injury_reported_by_name, role: selectedPlayer.injury_reported_by_role }"
                  :rtpStartDate="selectedPlayer.rtp_start_date" :maturityAtInjury="selectedPlayer.maturity_at_injury"
                  :phaseHistory="selectedPlayerPhaseHistory" :currentPhase="selectedPlayer.rtp_phase"
                  :daysInCurrentPhase="selectedPlayer.days_in_phase" :targetDate="selectedPlayer.rtp_target_date" />
              </div>

              <div v-if="detailTab === 'exercises'" class="tab-pane active">
                <RTPExerciseLibrary :currentPhase="selectedPlayer.rtp_phase"
                  :maturityCategory="selectedPlayer.maturity_at_injury" :injuryId="selectedPlayer.injury_id"
                  :playerId="selectedPlayer.player_id" :playerName="selectedPlayer.player_name" assignmentMode />
              </div>

              <div v-if="detailTab === 'injury-details'" class="tab-pane active">
                <div class="injury-details-content">
                  <div class="detail-section">
                    <h5><i class="bi bi-bandaid"></i> Injury Information</h5>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <label>Injury Type:</label>
                        <span>{{ selectedPlayer.injury_type || '-' }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Diagnosis:</label>
                        <span>{{ selectedPlayer.diagnosis || '-' }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Activity at Time:</label>
                        <span>{{ selectedPlayer.activity_at_time || '-' }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Injury Date:</label>
                        <span>{{ formatDate(selectedPlayer.injury_date) }}</span>
                      </div>
                      <div class="detail-item">
                        <label>Reported By:</label>
                        <span>{{ selectedPlayer.injury_reported_by_name || '-' }} ({{
                          selectedPlayer.injury_reported_by_role || '-' }})</span>
                      </div>
                      <div class="detail-item">
                        <label>Est. Return:</label>
                        <span>{{ selectedPlayer.estimate_return_date || '-' }}</span>
                      </div>
                      <div v-if="selectedPlayer.treatment" class="detail-item">
                        <label>Treatment:</label>
                        <span>{{ selectedPlayer.treatment }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="detail-section">
                    <h5><i class="bi bi-activity"></i> Current Status</h5>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <label>Playing Status:</label>
                        <span :class="selectedPlayer.playing_status == 1 ? 'status-good' : 'status-bad'">
                          {{ selectedPlayer.playing_status == 1 ? 'Ready to play' : "Can't play" }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <label>Training Status:</label>
                        <span :class="{
                          'status-good': selectedPlayer.training_status === 'Can fully train',
                          'status-warning': selectedPlayer.training_status === 'Can partially train',
                          'status-bad': selectedPlayer.training_status === 'Unable to train' || !selectedPlayer.training_status
                        }">
                          {{ selectedPlayer.training_status || 'None' }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <label>RTP Phase:</label>
                        <span class="phase-badge-sm"
                          :style="{ backgroundColor: getPhaseInfo(selectedPlayer.rtp_phase).color }">
                          Phase {{ selectedPlayer.rtp_phase }} - {{ getPhaseInfo(selectedPlayer.rtp_phase).name }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <label>Days on RTP:</label>
                        <span>{{ selectedPlayer.rtp_start_date ? getDaysSince(selectedPlayer.rtp_start_date) : '-' }}
                          days</span>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedPlayer.injury_notes" class="detail-section">
                    <h5><i class="bi bi-journal-text"></i> Notes</h5>
                    <p class="injury-notes">{{ selectedPlayer.injury_notes }}</p>
                  </div>

                  <div v-if="selectedPlayer.rtp_restrictions" class="detail-section">
                    <h5><i class="bi bi-exclamation-triangle"></i> Restrictions</h5>
                    <p class="restrictions-text">{{ selectedPlayer.rtp_restrictions }}</p>
                  </div>
                </div>
              </div>

              <!-- Physio Appointments Tab -->
              <div v-if="detailTab === 'physio-appointments'" class="tab-pane active">
                <div class="physio-appointments-content">
                  <div class="appointments-header">
                    <h5><i class="bi bi-calendar2-heart"></i> Physio Appointments</h5>
                    <router-link :to="{ name: 'bookPhysio' }" class="btn btn-sm btn-success">
                      <i class="bi bi-plus"></i> Book Appointment
                    </router-link>
                  </div>

                  <div v-if="appointmentsLoading" class="loading-state">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                    <span>Loading appointments...</span>
                  </div>

                  <div v-else-if="playerAppointments.length === 0" class="empty-appointments">
                    <i class="bi bi-calendar-x"></i>
                    <p>No physio appointments booked</p>
                    <router-link :to="{ name: 'bookPhysio' }" class="btn btn-primary btn-sm">
                      Book an Appointment
                    </router-link>
                  </div>

                  <div v-else class="appointments-list">
                    <div v-for="apt in playerAppointments" :key="apt.id" class="appointment-card" :class="apt.status">
                      <div class="apt-date-badge">
                        <span class="apt-day">{{ formatAptDay(apt.appointment_date) }}</span>
                        <span class="apt-month">{{ formatAptMonth(apt.appointment_date) }}</span>
                      </div>
                      <div class="apt-details">
                        <div class="apt-time">
                          <i class="bi bi-clock"></i>
                          {{ formatTime(apt.start_time) }} - {{ formatTime(apt.end_time) }}
                        </div>
                        <div class="apt-physio">
                          <i class="bi bi-person-badge"></i>
                          {{ apt.physio_name || 'Physio' }}
                        </div>
                        <div v-if="apt.reason" class="apt-reason">
                          <i class="bi bi-chat-text"></i>
                          {{ apt.reason }}
                        </div>
                      </div>
                      <div class="apt-status">
                        <span class="status-badge" :class="apt.status">
                          {{ apt.status }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-modal>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppTemplate from "../layouts/App";
import RTPPhaseTracker from "@/components/RTPPhaseTracker/index.vue";
import RTPTimeline from "@/components/RTPTimeline/index.vue";
import RTPExerciseLibrary from "@/components/RTPExerciseLibrary/index.vue";
import MyRTP from "@/components/MyRTP/index.vue";
import { ReturnToPlayService, RTP_PHASES } from "@/services/rtp-service.js";
import { Teams } from "@/services/teams-service.js";
import { PhysioCalendarService } from "@/services/physio-calendar-service.js";

export default {
  name: "ReturnToPlay",
  components: {
    AppTemplate,
    RTPPhaseTracker,
    RTPTimeline,
    RTPExerciseLibrary,
    MyRTP,
  },
  data() {
    return {
      loadIngStuffText: "Loading RTP Dashboard",
      loadingStuff: false,
      loading: true,

      // Data
      protocols: [],
      groupedProtocols: {},
      summary: {
        total: 0,
        byPhase: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        overdueReviews: 0,
        circaPhvCount: 0
      },
      teams: [],

      // Filters
      filterTeam: '',
      filterPhase: '',
      viewMode: 'grouped',
      sortField: 'rtp_phase',
      sortDirection: 'asc',

      // Modal
      selectedPlayer: null,
      showPlayerModal: false,
      selectedPlayerPhaseHistory: [],
      detailTab: 'tracker',

      // User data
      userData: null,

      // My RTP (for players)
      mainTab: 'dashboard', // 'dashboard' or 'my-exercises'
      playerHasActiveRTP: false,

      // Physio appointments
      playerAppointments: [],
      appointmentsLoading: false,
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isPlayer() {
      if (!this.userData) return false;
      const staffRoles = ['Admin', 'Coach', 'HeadCoach', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science', 'Scout'];
      return !staffRoles.includes(this.userData.userType) && !staffRoles.includes(this.userData.position);
    },
    canViewDashboard() {
      if (!this.userData) return false;
      const allowedRoles = ['Admin', 'Coach', 'HeadCoach', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science'];
      return allowedRoles.includes(this.userData.userType) || allowedRoles.includes(this.userData.position);
    },
    canManageExercises() {
      if (!this.userData) return false;
      const allowedRoles = ['Admin', 'Physio', 'Head Physio', 'Sports Science', 'Head Sports Science'];
      return allowedRoles.includes(this.userData.userType) || allowedRoles.includes(this.userData.position);
    },
    filteredProtocols() {
      let result = [...this.protocols];

      if (this.filterPhase) {
        result = result.filter(p => p.rtp_phase == this.filterPhase);
      }

      return result;
    },
    sortedProtocols() {
      const sorted = [...this.filteredProtocols];

      sorted.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return sorted;
    }
  },
  async mounted() {
    // Get user data from Vuex store (like other components do)
    if (this.getProfile !== null) {
      this.userData = this.getProfile;
    }

    if (!this.userData || !this.userData.clubId) {
      console.warn('No user data found');
      this.loading = false;
      return;
    }

    // Check if user is a player with active RTP
    if (this.isPlayer) {
      await this.checkPlayerActiveRTP();
      // Default to My Exercises tab for players with active RTP
      if (this.playerHasActiveRTP) {
        this.mainTab = 'my-exercises';
      }
    }

    // Load dashboard data for staff
    if (this.canViewDashboard) {
      await this.loadTeams();
      await this.loadDashboardData();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async checkPlayerActiveRTP() {
      if (!this.userData?.id) return;
      try {
        const response = await ReturnToPlayService.getPlayerActiveRTP(this.userData.id);
        this.playerHasActiveRTP = response.data.success && response.data.has_active_rtp;
      } catch (error) {
        console.error('Error checking player RTP:', error);
        this.playerHasActiveRTP = false;
      }
    },

    async updatePlayerStatus(field, value) {
      if (!this.selectedPlayer?.injury_id) return;

      try {
        const data = {
          injuryId: this.selectedPlayer.injury_id
        };

        if (field === 'playing_status') {
          data.playingStatus = value;
        } else if (field === 'training_status') {
          data.trainingStatus = value;
        }

        const response = await ReturnToPlayService.updateRTPStatus(data);
        if (response.data?.success) {
          this.$toast?.success('Status updated successfully');
          // Also update in the protocols array
          const idx = this.protocols.findIndex(p => p.injury_id === this.selectedPlayer.injury_id);
          if (idx !== -1) {
            this.protocols[idx][field] = value;
          }
        } else {
          this.$toast?.error(response.data?.message || 'Failed to update status');
        }
      } catch (error) {
        console.error('Error updating status:', error);
        this.$toast?.error('Error updating status');
      }
    },

    getDaysSince(dateStr) {
      if (!dateStr) return 0;
      const date = new Date(dateStr);
      const today = new Date();
      const diffTime = today - date;
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    },

    getPhaseInfo(phase) {
      return RTP_PHASES[phase] || { name: 'Unknown', color: '#6c757d', bgColor: '#f8f9fa', description: '' };
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    async loadTeams() {
      if (!this.userData?.clubId) return;

      try {
        const response = await Teams.find({
          action: "getTeamsDataById",
          clubId: this.userData.clubId
        });
        this.teams = response.data || [];
      } catch (error) {
        console.error('Error loading teams:', error);
      }
    },

    async loadDashboardData() {
      if (!this.userData?.clubId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const params = {
          clubId: this.userData.clubId
        };

        if (this.filterTeam) {
          params.teamId = this.filterTeam;
        }

        const response = await ReturnToPlayService.getRTPDashboard(params);

        if (response.data.success) {
          this.protocols = response.data.protocols || [];
          this.groupedProtocols = response.data.grouped || {};
          this.summary = response.data.summary || {
            total: 0,
            byPhase: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
            overdueReviews: 0,
            circaPhvCount: 0
          };
        }
      } catch (error) {
        console.error('Error loading RTP dashboard:', error);
      } finally {
        console.log('Finished loading RTP dashboard');
        this.loading = false;
      }
    },

    refreshData() {
      this.loadDashboardData();
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    async openPlayerDetail(player) {
      this.selectedPlayer = player;
      this.detailTab = 'tracker';
      this.showPlayerModal = true;
      this.playerAppointments = []; // Reset appointments

      // Load phase history
      try {
        const response = await ReturnToPlayService.getRTPByInjuryId(player.injury_id);
        if (response.data.success) {
          this.selectedPlayerPhaseHistory = response.data.phase_history || [];
        }
      } catch (error) {
        console.error('Error loading phase history:', error);
      }

      // Pre-load physio appointments for badge count
      this.loadPlayerAppointments();
    },

    onPhaseUpdated(data) {
      this.loadDashboardData();
      // Refresh the selected player data
      if (this.selectedPlayer) {
        this.openPlayerDetail(this.selectedPlayer);
      }
    },

    onRtpCompleted(data) {
      this.showPlayerModal = false;
      this.selectedPlayer = null;
      this.loadDashboardData();
    },

    exportToCsv() {
      const headers = ['Player', 'Team', 'Injury', 'Phase', 'Days in Phase', 'Phase Lead', 'Maturity', 'Review Status'];
      const rows = this.protocols.map(p => [
        p.player_name,
        p.teamName || p.age_group,
        p.injury_type,
        `P${p.rtp_phase}`,
        p.days_in_phase || 0,
        p.current_phase_lead || '-',
        p.maturity_at_injury || '-',
        p.days_until_review !== null ? (p.days_until_review < 0 ? `${Math.abs(p.days_until_review)}d overdue` : `${p.days_until_review}d`) : '-'
      ]);

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rtp-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    },

    // Physio Appointments Methods
    async loadPlayerAppointments() {
      if (!this.selectedPlayer?.player_id) return;

      this.appointmentsLoading = true;
      try {
        const response = await PhysioCalendarService.getPlayerAppointments({
          player_id: this.selectedPlayer.player_id
        });

        if (Array.isArray(response.data)) {
          this.playerAppointments = response.data;
        } else if (response.data?.appointments) {
          this.playerAppointments = response.data.appointments;
        } else {
          this.playerAppointments = [];
        }
      } catch (error) {
        console.error('Error loading player appointments:', error);
        this.playerAppointments = [];
      } finally {
        this.appointmentsLoading = false;
      }
    },

    formatAptDay(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.getDate();
    },

    formatAptMonth(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { month: 'short' });
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes}${ampm}`;
    }
  },
};
</script>

<style scoped lang="scss">
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-content h1 {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    color: #007bff;
  }
}

.header-content .subtitle {
  color: #6c757d;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* Main Tab Navigation */
.main-tab-nav {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  padding: 4px;
  background: #f8f9fa;
  border-radius: 10px;
  width: fit-content;
}

.main-tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    background: rgba(0, 123, 255, 0.1);
    color: #007bff;
  }

  &.active {
    background: white;
    color: #007bff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
}

.my-exercises-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.exercise-library-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Status Bar in Player Modal */
.status-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #666;
    margin: 0;
  }

  select {
    min-width: 130px;
  }

  .status-value {
    font-weight: 600;
    color: #333;
  }
}

/* Injury Details Tab */
.injury-details-content {
  padding: 16px 0;
}

.detail-section {
  margin-bottom: 24px;

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;

    i {
      color: #007bff;
    }
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
  }

  span {
    font-size: 14px;
    color: #333;
  }
}

.status-good {
  color: #28a745 !important;
  font-weight: 600;
}

.status-warning {
  color: #ffc107 !important;
  font-weight: 600;
}

.status-bad {
  color: #dc3545 !important;
  font-weight: 600;
}

.injury-notes,
.restrictions-text {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 0;
}

.restrictions-text {
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
}

/* Summary Stats */
.summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 140px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
}

.stat-card.total .stat-icon {
  background: #e3f2fd;
  color: #1976d2;
}

.stat-card.overdue .stat-icon {
  background: #ffebee;
  color: #d32f2f;
}

.stat-card.circa-phv .stat-icon {
  background: #fff3e0;
  color: #f57c00;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
}

/* Filters */
.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
}

.filter-group select {
  min-width: 160px;
}

.view-toggle .btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.loading-state .spinner-border {
  width: 3rem;
  height: 3rem;
}

.empty-state i {
  font-size: 64px;
  color: #dee2e6;
  margin-bottom: 16px;
}

.empty-state h4 {
  margin-bottom: 8px;
}

.empty-state p {
  color: #6c757d;
  margin: 0;
}

/* Grouped View */
.grouped-view {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.phase-group {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.phase-group.empty {
  opacity: 0.6;
}

.group-header {
  border-left: 4px solid #6c757d;
  padding-left: 16px;
  margin-bottom: 16px;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.phase-badge {
  padding: 4px 12px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.group-title h3 {
  margin: 0;
  font-size: 18px;
}

.player-count {
  font-size: 14px;
  color: #6c757d;
}

.group-description {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.players-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.player-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 2px solid transparent;
}

.player-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.player-card.overdue {
  border-color: #dc3545;
  background: #fff5f5;
}

.player-card.circa-phv {
  border-color: #ffc107;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.player-avatar,
.player-avatar-sm {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.player-avatar-sm {
  width: 32px;
  height: 32px;
}

.player-avatar-placeholder,
.player-avatar-placeholder-sm {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #616161;
}

.player-avatar-placeholder-sm {
  width: 32px;
  height: 32px;
  font-size: 12px;
}

.player-info {
  flex: 1;
}

.player-name {
  display: block;
  font-weight: 600;
  font-size: 14px;
}

.team-name {
  display: block;
  font-size: 12px;
  color: #6c757d;
}

.maturity-flag {
  color: #f57c00;
  font-size: 18px;
}

.injury-type {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #495057;
  margin-bottom: 12px;

  i {
    color: #dc3545;
  }
}

.card-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.card-stats .stat {
  display: flex;
  flex-direction: column;
}

.card-stats .stat-label {
  font-size: 10px;
  color: #9e9e9e;
  text-transform: uppercase;
}

.card-stats .stat-value {
  font-size: 14px;
  font-weight: 600;
}

.card-stats .stat-value.warning {
  color: #f57c00;
}

.review-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 6px 10px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 6px;
}

.review-status.overdue {
  background: #ffebee;
  color: #c62828;
}

.no-players {
  text-align: center;
  padding: 20px;
  color: #9e9e9e;
}

/* Table View */
.table-view {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.rtp-table {
  width: 100%;
  border-collapse: collapse;
}

.rtp-table th,
.rtp-table td {
  padding: 12px 16px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid #e9ecef;
}

.rtp-table th {
  background: #f8f9fa;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
}

.rtp-table th.sortable {
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #e9ecef;
  }
}

.player-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.phase-badge-sm {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  color: white;
  font-size: 11px;
  font-weight: 600;
}

.maturity-badge-sm {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.maturity-prephv {
  background: #e0f7fa;
  color: #006064;
}

.maturity-circaphv {
  background: #fff3e0;
  color: #e65100;
}

.maturity-postphv {
  background: #e8f5e9;
  color: #1b5e20;
}

.overdue-row {
  background: #fff5f5;
}

.circa-phv-row {
  background: #fffde7;
}

/* Modal */
.player-detail-content {
  padding: 0;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 16px;
}

.player-profile {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profile-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  color: #616161;
}

.profile-info h4 {
  margin: 0 0 4px 0;
}

.profile-info p {
  margin: 0;
  color: #6c757d;
}

.injury-info {
  text-align: right;
}

.injury-info .injury-type {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.injury-info .injury-date {
  display: block;
  font-size: 13px;
  color: #6c757d;
}

.detail-tabs {
  margin-bottom: 20px;
}

.detail-tabs .nav-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6c757d;

  &.active {
    color: #007bff;
    font-weight: 500;
  }
}

/* Physio Appointments Tab */
.physio-appointments-content {
  padding: 16px 0;
}

.appointments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 16px;
    font-weight: 600;

    i {
      color: #007bff;
    }
  }
}

.empty-appointments {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;

  i {
    font-size: 48px;
    opacity: 0.4;
    margin-bottom: 12px;
    display: block;
  }

  p {
    margin-bottom: 16px;
    font-size: 14px;
  }
}

.appointments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.appointment-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.confirmed,
  &.booked {
    border-left: 4px solid #28a745;
  }

  &.pending {
    border-left: 4px solid #ffc107;
  }

  &.cancelled {
    border-left: 4px solid #dc3545;
    opacity: 0.7;
  }
}

.apt-date-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #f8f9fa;
  border-radius: 10px;
  flex-shrink: 0;

  .apt-day {
    font-size: 22px;
    font-weight: 700;
    color: #333;
    line-height: 1;
  }

  .apt-month {
    font-size: 11px;
    color: #6c757d;
    text-transform: uppercase;
    font-weight: 600;
  }
}

.apt-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .apt-time,
  .apt-physio,
  .apt-reason {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #555;

    i {
      color: #6c757d;
      font-size: 14px;
      width: 20px;
    }
  }

  .apt-time {
    font-weight: 600;
    color: #333;
  }

  .apt-reason {
    font-style: italic;
    color: #6c757d;
  }
}

.apt-status {
  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;

    &.confirmed,
    &.booked {
      background: #d4edda;
      color: #155724;
    }

    &.pending {
      background: #fff3cd;
      color: #856404;
    }

    &.cancelled {
      background: #f8d7da;
      color: #721c24;
    }
  }
}

.appointment-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #28a745;
  color: white;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 6px;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    justify-content: flex-end;
  }

  .summary-stats {
    flex-direction: column;
  }

  .stat-card {
    width: 100%;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group select {
    width: 100%;
  }

  .players-grid {
    grid-template-columns: 1fr;
  }

  .table-view {
    overflow-x: auto;
  }

  .rtp-table {
    min-width: 800px;
  }
}
</style>
